import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { KeyRound, Mail, RefreshCw, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = window.location.origin;

const ForgotPasswordPage = () => {
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'newpass'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const text = await res.text();
      const data = JSON.parse(text);
      if (!res.ok) throw new Error(data.detail || 'Failed to send code');
      toast.success('Reset code sent! Check your email.');
      setCooldown(60);
      setStep('code');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const handleCodeSubmit = (e) => {
    e?.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) { toast.error('Please enter the full 6-digit code'); return; }
    setStep('newpass');
  };

  // Auto-advance when all 6 digits entered
  useEffect(() => {
    if (step === 'code' && code.every(d => d !== '')) {
      setStep('newpass');
    }
  }, [code, step]);

  const handleResend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const text = await res.text();
      const data = JSON.parse(text);
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('New code sent!');
      setCooldown(60);
      setCode(['', '', '', '', '', '']);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: code.join(''), new_password: newPassword }),
      });
      const text = await res.text();
      const data = JSON.parse(text);
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      toast.success('Password reset! You can now sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030014] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1565590915406-9c386cc9391d?w=1920)' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/80 to-[#030014]/60" />
      </div>
      <Link to="/" className="absolute top-6 left-6 z-10">
        <img src="/reframes-logo.png" alt="reframes" className="h-9 md:h-11" />
      </Link>

      <div className="relative z-10 w-full max-w-md">
        <div className="admin-card p-8">
          {/* Back to login */}
          <button
            onClick={() => step === 'email' ? navigate('/login') : setStep(step === 'newpass' ? 'code' : 'email')}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 'email' ? 'Back to sign in' : 'Back'}
          </button>

          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[#8B5CF6]/20 flex items-center justify-center">
            <KeyRound className="w-8 h-8 text-[#8B5CF6]" />
          </div>

          {/* Step 1: Enter email */}
          {step === 'email' && (
            <>
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Forgot Password?
              </h1>
              <p className="text-gray-400 text-sm text-center mb-6">
                Enter your email and we'll send you a code to reset your password.
              </p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="send-code-btn">
                  {loading ? <div className="spinner w-5 h-5" /> : 'Send Reset Code'}
                </Button>
              </form>
            </>
          )}

          {/* Step 2: Enter 6-digit code */}
          {step === 'code' && (
            <>
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Enter Reset Code
              </h1>
              <p className="text-gray-400 text-sm text-center mb-2">We sent a 6-digit code to</p>
              <p className="text-white font-medium text-center mb-6" data-testid="reset-email-display">{email}</p>

              <form onSubmit={handleCodeSubmit}>
                <div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
                  {code.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => inputRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      className="w-12 h-14 text-center text-xl font-bold bg-[#030014] border-2 border-white/10 rounded-xl text-white focus:border-[#8B5CF6] focus:outline-none transition-colors"
                      data-testid={`reset-code-input-${i}`}
                    />
                  ))}
                </div>
                <Button type="submit" disabled={code.some(d => d === '')} className="w-full btn-primary h-12" data-testid="verify-reset-code-btn">
                  Continue
                </Button>
              </form>

              <div className="mt-6">
                <button
                  onClick={handleResend}
                  disabled={loading || cooldown > 0}
                  className="flex items-center gap-2 mx-auto text-sm text-gray-400 hover:text-[#8B5CF6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="resend-reset-code-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">Code expires in 10 minutes</p>
            </>
          )}

          {/* Step 3: Set new password */}
          {step === 'newpass' && (
            <>
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Set New Password
              </h1>
              <p className="text-gray-400 text-sm text-center mb-6">
                Choose a strong new password for your account.
              </p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10 pr-10 bg-[#030014] border-[#8B5CF6]/20 h-12"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10 bg-[#030014] border-[#8B5CF6]/20 h-12"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary h-12" data-testid="reset-password-btn">
                  {loading ? <div className="spinner w-5 h-5" /> : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
